import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import api from '../api/api';
import logo from "../assets/logo.webp";
import { Smartphone } from 'lucide-react';
import notif from '../utils/notificationService';

// "NEW" flag for freshly shipped menu items.
//
// It expires on its own: pass the ship date and the badge stops rendering
// NEW_BADGE_DAYS later, so nobody has to remember a follow-up commit to remove
// it. Anyone who first opens the menu after the window simply never sees it,
// which is the point — it announces a change, it is not a permanent label.
const NEW_BADGE_DAYS = 5;

const isRecentlyShipped = (shippedOn) => {
    const t = Date.parse(`${shippedOn}T00:00:00`);
    if (Number.isNaN(t)) return false;
    return Date.now() < t + NEW_BADGE_DAYS * 24 * 60 * 60 * 1000;
};

const NewBadge = () => (
    <span className="ml-1.5 inline-block align-middle bg-emerald-500 text-white rounded-full px-1.5 py-[1px] text-[8px] font-extrabold uppercase tracking-wider leading-[1.4]">
        new
    </span>
);

const Navbar = () => {
    const { user, logout } = useAuth();
    const [profile, setProfile] = useState(null);
    const [hasUnread, setHasUnread] = useState(false);
    const [pendingLeavesCount, setPendingLeavesCount] = useState(0);
    const [pendingPartnersCount, setPendingPartnersCount] = useState(0);
    const [taskNotif, setTaskNotif] = useState({ pending: 0, overdue: 0, open: 0 });
    const [meetingNotif, setMeetingNotif] = useState({ today: 0, upcoming: 0, startingSoon: 0, happeningNow: 0, nextMeeting: null });
    const [projectNotif, setProjectNotif] = useState({ ongoing: 0, overdue: 0, awaitingMySignOff: 0 });
    const [assetNotif, setAssetNotif] = useState({ myPending: 0, teamPending: 0, totalPending: 0 });
    // Admin-only calls badge — counts pending-soon (next hour) + due-today
    // + overdue so the founder sees one glanceable number on the navbar pill.
    const [callsNotif, setCallsNotif] = useState({ dueToday: 0, overdue: 0, pendingSoon: 0 });
    // Admin/HR — count of NEW (untriaged) portal requests for the navbar badge.
    const [portalReqNew, setPortalReqNew] = useState(0);
    // Targets dropdown — current-month targets for the logged-in user (or their team).
    const [targetsOpen, setTargetsOpen] = useState(false);
    const [targetsData, setTargetsData] = useState(null); // { month, year, targets: [] }
    const [targetsLoading, setTargetsLoading] = useState(false);
    const targetsTriggerRef = React.useRef(null);
    const [partnersOpen, setPartnersOpen] = useState(false);
    const [approvedPartners, setApprovedPartners] = useState([]);
    const partnersTriggerRef = React.useRef(null);
    // Billing access is gated server-side; we ask the server "can I see Billing?"
    // and only render the link if yes. Keeps the nav clean for everyone else.
    const [billingPerm, setBillingPerm] = useState({ canCreate: false, canViewAll: false, canViewOwn: false });
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    // HR mega-menu state — lives at the navbar level so we can render the
    // panel as `position: fixed` outside the scrolling nav row. (Hover-based
    // CSS dropdowns got clipped by overflow-x:auto on the scroll wrapper.)
    const [hrMenuOpen, setHrMenuOpen] = useState(false);
    const [hrMenuPos, setHrMenuPos] = useState({ top: 0, right: 0 });
    const hrTriggerRef = React.useRef(null);
    const location = useLocation();

    // Recompute the mega-menu's anchor whenever it opens or the viewport size
    // changes — keeps it pinned just under the "Human Resources" button.
    React.useEffect(() => {
        if (!hrMenuOpen) return;
        const place = () => {
            const r = hrTriggerRef.current?.getBoundingClientRect();
            if (!r) return;
            setHrMenuPos({
                top: r.bottom + 8,
                right: Math.max(8, window.innerWidth - r.right)
            });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [hrMenuOpen]);

    // Click anywhere outside the menu / its trigger → close.
    React.useEffect(() => {
        if (!hrMenuOpen) return;
        const onDocClick = (e) => {
            if (hrTriggerRef.current?.contains(e.target)) return;
            const panel = document.getElementById('hr-mega-menu');
            if (panel && panel.contains(e.target)) return;
            setHrMenuOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [hrMenuOpen]);

    // Targets dropdown: lazy-load on first open, close on outside click.
    React.useEffect(() => {
        if (!targetsOpen) return;
        if (!targetsData && !targetsLoading) {
            setTargetsLoading(true);
            api.get('/targets/navbar')
                .then(r => setTargetsData(r.data))
                .catch(() => setTargetsData({ targets: [] }))
                .finally(() => setTargetsLoading(false));
        }
        const onDocClick = (e) => {
            if (targetsTriggerRef.current?.contains(e.target)) return;
            const panel = document.getElementById('targets-dropdown');
            if (panel && panel.contains(e.target)) return;
            setTargetsOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [targetsOpen, targetsData, targetsLoading]);

    React.useEffect(() => {
        if (!partnersOpen) return;
        // Also lazy-load targets data into the combined Leads dropdown
        if (!targetsData && !targetsLoading) {
            setTargetsLoading(true);
            api.get('/targets/navbar')
                .then(r => setTargetsData(r.data))
                .catch(() => setTargetsData({ targets: [] }))
                .finally(() => setTargetsLoading(false));
        }
        const onDocClick = (e) => {
            if (partnersTriggerRef.current?.contains(e.target)) return;
            const panel = document.getElementById('partners-dropdown');
            if (panel && panel.contains(e.target)) return;
            setPartnersOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [partnersOpen, targetsData, targetsLoading]);

    // Helper class for mobile links to match desktop style
    const mobileNavLinkClass = ({ isActive }) =>
        `block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 ${isActive ? 'bg-[#8a6144] text-white shadow-md' : isScrolled ? 'text-gray-700 hover:bg-[#8a6144]/10 hover:text-[#8a6144]' : 'text-gray-300 hover:bg-white/10 hover:text-white'}`;

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const fetchNavData = async () => {
            if (!user) return;
            // Partners have their own protected routes — none of the employee/HR
            // notification endpoints accept a partner token, so skip them all to
            // avoid triggering the global 401 "session expired" redirect.
            if (user.role === 'partner') return;

            // Fire ALL badge requests in parallel — was 7 sequential round-trips.
            const [
                taskRes, meetingRes, projectRes, assetRes,
                billingRes, callsRes, roleRes, partnerRes, portalReqRes
            ] = await Promise.allSettled([
                api.get('/tasks/notifications'),
                api.get('/meetings/notifications'),
                api.get('/projects/notifications'),
                api.get('/assets/notifications'),
                api.get('/invoices/permissions'),
                api.get('/calls/stats'),
                user.role === 'employee'
                    ? Promise.all([api.get('/employee/profile'), api.get('/employee/announcements')])
                    : api.get('/hr/leaves/pending'),
                user.role === 'admin' || user.role === 'hr'
                    ? api.get('/hr/partners')
                    : Promise.resolve(null),
                user.role === 'admin' || user.role === 'hr'
                    ? api.get('/portal-requests/summary')
                    : Promise.resolve(null)
            ]);

            // Tasks
            if (taskRes.status === 'fulfilled' && taskRes.value) {
                const data = taskRes.value.data;
                const pending = data.pending || 0;
                const overdue = data.overdue || 0;
                const lastPending = notif.getSeen('nav.task.pending') ?? pending;
                if (pending > lastPending) {
                    notif.notify({ type: 'task', title: '📋 New task assigned',
                        body: `You have ${pending} pending task${pending === 1 ? '' : 's'}`,
                        url: '/hr/tasks', tag: 'task-new' });
                }
                notif.markSeen('nav.task.pending', pending);
                setTaskNotif({ pending, overdue, open: data.open || 0 });
            }

            // Meetings
            if (meetingRes.status === 'fulfilled' && meetingRes.value) {
                const data = meetingRes.value.data;
                const upcoming = data.upcoming || 0;
                const startingSoon = data.startingSoon || 0;
                const happeningNow = data.happeningNow || 0;
                const lastUpcoming = notif.getSeen('nav.mtg.upcoming') ?? upcoming;
                if (upcoming > lastUpcoming) {
                    notif.notify({ type: 'meeting', title: '🗓️ New meeting scheduled',
                        body: data.nextMeeting?.title ? `${data.nextMeeting.title} — check the Meetings tab` : 'A new meeting was added to your calendar',
                        url: '/hr/meetings', tag: 'mtg-new' });
                }
                notif.markSeen('nav.mtg.upcoming', upcoming);
                const lastSoonId = notif.getSeen('nav.mtg.soonId');
                const nextId = data.nextMeeting?._id || null;
                if ((startingSoon > 0 || happeningNow > 0) && nextId && String(nextId) !== String(lastSoonId)) {
                    notif.notify({ type: 'meeting-soon', title: '⏰ Meeting starting soon',
                        body: data.nextMeeting?.title ? `"${data.nextMeeting.title}" is starting now — join from the portal` : 'You have a meeting starting within the next 5 minutes',
                        url: '/hr/meetings', tag: 'mtg-soon', forceVisible: true });
                    notif.markSeen('nav.mtg.soonId', String(nextId));
                }
                setMeetingNotif({ today: data.today || 0, upcoming, startingSoon, happeningNow, nextMeeting: data.nextMeeting || null });
            }

            // Projects
            if (projectRes.status === 'fulfilled' && projectRes.value) {
                const data = projectRes.value.data;
                setProjectNotif({ ongoing: data.ongoing || 0, overdue: data.overdue || 0, awaitingMySignOff: data.awaitingMySignOff || 0 });
            }

            // Assets
            if (assetRes.status === 'fulfilled' && assetRes.value) {
                const data = assetRes.value.data;
                setAssetNotif({ myPending: data.myPending || 0, teamPending: data.teamPending || 0, totalPending: data.totalPending || 0 });
            }

            // Billing
            if (billingRes.status === 'fulfilled' && billingRes.value) {
                const data = billingRes.value.data;
                setBillingPerm({ canCreate: !!data.canCreate, canViewAll: !!data.canViewAll, canViewOwn: !!data.canViewOwn });
            }

            // Calls (admin only — callsRes.value is null for non-admins)
            if (callsRes.status === 'fulfilled' && callsRes.value) {
                const data = callsRes.value.data;
                const dueToday = data.dueToday || 0;
                const overdue = data.overdue || 0;
                const pendingSoon = data.pendingSoon || 0;
                const lastSoon = notif.getSeen('nav.calls.soon') ?? 0;
                if (pendingSoon > lastSoon) {
                    notif.notify({ type: 'call-soon', title: '📞 Call coming up soon',
                        body: `${pendingSoon} call${pendingSoon === 1 ? '' : 's'} scheduled within the next hour`,
                        url: '/admin/calls', tag: 'call-soon', forceVisible: true });
                }
                notif.markSeen('nav.calls.soon', pendingSoon);
                setCallsNotif({ dueToday, overdue, pendingSoon });
            }

            // Role-specific (employee profile/announcements OR hr leaves)
            if (roleRes.status === 'fulfilled' && roleRes.value) {
                if (user.role === 'employee') {
                    const [profileRes2, annRes] = roleRes.value;
                    setProfile(profileRes2.data);
                    setHasUnread(annRes.data.some((ann) => !ann.isRead));
                } else {
                    setPendingLeavesCount(roleRes.value.data?.length || 0);
                }
            }

            // Partners (HR/Admin)
            if (partnerRes && partnerRes.status === 'fulfilled' && partnerRes.value) {
                const allPartners = partnerRes.value.data;
                const pendingPartners = allPartners.filter(p => p.status === 'pending').length;
                setPendingPartnersCount(pendingPartners);
                setApprovedPartners(allPartners.filter(p => p.status === 'approved'));
            }

            // Portal requests (HR/Admin) — show count of NEW (untriaged) requests.
            if (portalReqRes && portalReqRes.status === 'fulfilled' && portalReqRes.value) {
                const data = portalReqRes.value.data || {};
                const newCount = data.new || 0;
                const lastSeen = notif.getSeen('nav.portalReq.new') ?? 0;
                if (newCount > lastSeen) {
                    notif.notify({ type: 'portal-request', title: '🔔 New portal request',
                        body: `${newCount} new request${newCount === 1 ? '' : 's'} waiting for review`,
                        url: '/hr/portal-requests', tag: 'portal-req-new' });
                }
                notif.markSeen('nav.portalReq.new', newCount);
                setPortalReqNew(newCount);
            }
        };
        fetchNavData();
    }, [user, location.pathname]);

    // Pill-style nav link — matches the public-nav pill aesthetic. Active
    // state gets a filled brown pill (cream text on the dark navbar, white
    // text once the navbar has scrolled to the cream variant). Hover gets a
    // subtle translucent bg.
    const navLinkClass = ({ isActive }) =>
        `relative px-3 lg:px-3.5 py-1.5 rounded-full text-[10.5px] lg:text-xs font-bold whitespace-nowrap flex-shrink-0 transition-all duration-300 ${
            isActive
                ? (isScrolled
                    ? 'bg-gradient-to-br from-[#433020] to-[#2d2017] text-white shadow-md'
                    : 'bg-[#fff5e6] text-[#433020] shadow-md')
                : (isScrolled
                    ? 'text-[#5d4332] hover:text-[#433020] hover:bg-[#fff5e6]'
                    : 'text-white/90 hover:text-white hover:bg-white/10')
        }`;

    const handleLinkClick = () => setMobileMenuOpen(false);

    const buttonStyle = `px-4 py-2 text-[10px] lg:text-xs rounded-full transition-all duration-300 font-bold border-2 border-transparent shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 ${isScrolled ? 'bg-[#8a6144] text-white hover:bg-[#6b4d36]' : 'bg-[#fff5e6] text-[#433020] hover:bg-white'}`;

    return (
        <nav className={`fixed top-0 left-0 w-full z-50 font-sans transition-colors duration-300 shadow-md ${isScrolled ? 'bg-[#fff5e6] py-2' : 'bg-[#433020] py-3'}`}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative flex items-center justify-between h-14">
                    <Link to="/" className="flex items-center gap-3 transition-transform duration-300 hover:scale-105 flex-shrink-0">
                        <img src={logo} alt="Avani Business OS Logo" width="200" height="200" className={`w-auto rounded-xl shadow-md transition-all duration-300 ${isScrolled ? 'h-10' : 'h-12'}`} />
                        <div className="flex flex-col justify-center leading-none whitespace-nowrap">
                            <span className={`text-base md:text-lg font-black tracking-widest uppercase transition-colors duration-300 ${isScrolled ? 'text-[#433020]' : 'text-white'}`}>AVANI</span>
                            <span className={`text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase transition-colors duration-300 ${isScrolled ? 'text-[#8a6144]' : 'text-gray-300'}`}>
                                BUSINESS <span className={`inline-block px-1 rounded-sm ${isScrolled ? 'bg-[#8a6144] text-white' : 'bg-[#fff5e6] text-[#433020]'}`}>OS</span>
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Menu — visible from md (768px) upwards, ALWAYS
                        horizontally scrollable. Wrapped in a relative parent
                        that paints a soft fade on the right edge so users know
                        more items are off-screen. flex-1 min-w-0 lets it shrink
                        below content size so the inner row actually overflows
                        and triggers scroll instead of pushing the auth buttons
                        out of the navbar. */}
                    <div className={`hidden md:block relative flex-1 min-w-0 ml-4 overflow-hidden scroll-fade-right ${isScrolled ? '' : 'is-scrolled-dark'}`}>
                        <div
                            className="flex items-center gap-1 overflow-x-scroll no-scrollbar whitespace-nowrap"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                        >
                        {user ? (
                            <>
                                {(user.role === 'hr' || user.role === 'admin') && (
                                    <>
                                        <NavLink to="/hr/dashboard" className={navLinkClass}>Dashboard</NavLink>
                                        <NavLink to="/hr/tasks" className={navLinkClass}>
                                            <TaskLabel pending={taskNotif.pending} overdue={taskNotif.overdue} />
                                        </NavLink>
                                        {user.role === 'admin' && (
                                            <>
                                                <NavLink to="/hr/leads" className={navLinkClass}>Leads</NavLink>
                                                <button
                                                    ref={partnersTriggerRef}
                                                    type="button"
                                                    onClick={() => setPartnersOpen(v => !v)}
                                                    title="Leads menu"
                                                    className={`${navLinkClass({ isActive: partnersOpen })} !px-2 flex-shrink-0`}
                                                >
                                                    <svg className={`w-3 h-3 transition-transform ${partnersOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </button>
                                            </>
                                        )}
                                        <NavLink to="/hr/meetings" className={navLinkClass}>
                                            <MeetingLabel notif={meetingNotif} />
                                        </NavLink>
                                        <NavLink to="/hr/projects" className={navLinkClass}>
                                            <ProjectLabel notif={projectNotif} />
                                        </NavLink>
                                        <NavLink to="/hr/expenses" className={navLinkClass}>Expenses</NavLink>
                                        <NavLink to="/hr/assets" className={navLinkClass}>
                                            <AssetLabel notif={assetNotif} />
                                        </NavLink>
                                        {(billingPerm.canCreate || billingPerm.canViewAll) && (
                                            <NavLink to="/hr/invoices" className={navLinkClass}>Billing</NavLink>
                                        )}
                                        <NavLink to="/hr/kpis" className={navLinkClass}>KPIs</NavLink>
                                        {user.role === 'admin' && (
                                            <NavLink to="/hr/finance" className={navLinkClass}>
                                                <span className="inline-flex items-center gap-1">
                                                    Finance
                                                    <span className="text-[8px] font-extrabold uppercase tracking-widest bg-amber-500/20 text-amber-700 border border-amber-500/40 rounded-sm px-1 py-0">A</span>
                                                </span>
                                            </NavLink>
                                        )}
                                        <NavLink to="/admin/calls" className={navLinkClass}>
                                            <CallsLabel notif={callsNotif} />
                                        </NavLink>
                                        <NavLink to="/hr/email" className={navLinkClass}>Email CRM</NavLink>

                                        {/* HR Operations — click-controlled trigger.
                                            The actual mega-menu panel is rendered
                                            at the bottom of the navbar as a
                                            position:fixed element so it escapes
                                            the overflow-x:auto clip of this
                                            scrolling nav row. */}
                                        <button
                                            ref={hrTriggerRef}
                                            type="button"
                                            onClick={() => setHrMenuOpen((v) => !v)}
                                            className={`${navLinkClass({ isActive: false })} flex items-center gap-1 flex-shrink-0`}
                                            aria-expanded={hrMenuOpen}
                                            aria-haspopup="true"
                                        >
                                            Human Resources
                                            <svg className={`w-4 h-4 transition-transform ${hrMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                            </svg>
                                        </button>
                                    </>
                                )}

                                {user.role === 'partner' && (
                                    <>
                                        <NavLink to="/partner/dashboard" className={navLinkClass}>Dashboard</NavLink>
                                    </>
                                )}

                                {user.role === 'employee' && (
                                    <>
                                        <NavLink to="/employee/dashboard" className={navLinkClass}>Dashboard</NavLink>
                                        <NavLink to="/employee/tasks" className={navLinkClass}>
                                            <TaskLabel pending={taskNotif.pending} overdue={taskNotif.overdue} />
                                        </NavLink>
                                        <NavLink to="/employee/leads" className={navLinkClass}>Leads</NavLink>
                                        {user.isManager && user.hasLeadsAccess && (
                                            <button
                                                ref={partnersTriggerRef}
                                                type="button"
                                                onClick={() => setPartnersOpen(v => !v)}
                                                title="Leads & Targets"
                                                className={`${navLinkClass({ isActive: partnersOpen })} !px-2 flex-shrink-0`}
                                            >
                                                <svg className={`w-3 h-3 transition-transform ${partnersOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                        )}
                                        <NavLink to="/employee/meetings" className={navLinkClass}>
                                            <MeetingLabel notif={meetingNotif} />
                                        </NavLink>
                                        <NavLink to="/employee/projects" className={navLinkClass}>
                                            <ProjectLabel notif={projectNotif} />
                                        </NavLink>
                                        <NavLink to="/employee/expenses" className={navLinkClass}>Expenses</NavLink>
                                        <NavLink to="/employee/assets" className={navLinkClass}>
                                            <AssetLabel notif={assetNotif} />
                                        </NavLink>
                                        {(billingPerm.canCreate || billingPerm.canViewAll) && (
                                            <NavLink to="/employee/invoices" className={navLinkClass}>Billing</NavLink>
                                        )}
                                        {profile?.hasFinanceAccess && (
                                            <NavLink to="/employee/finance" className={navLinkClass}>Finance</NavLink>
                                        )}
                                        {user.isManager && (
                                            <NavLink to="/employee/kpis" className={navLinkClass}>KPIs</NavLink>
                                        )}
                                        <NavLink to="/employee/rankings" className={navLinkClass}>Rankings</NavLink>
                                        <NavLink to={(user.canBlog || profile?.canBlog) ? '/employee/blog' : '/blog'} className={navLinkClass}>Blog</NavLink>
                                        <NavLink to="/employee/apply-leave" className={navLinkClass}>Apply for Leave</NavLink>
                                        <NavLink to="/employee/announcements" className={navLinkClass}>
                                            <span className="relative">
                                                Announcements
                                                {hasUnread && (
                                                    <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 h-2 w-2 bg-red-500 rounded-full animate-ping"></span>
                                                )}
                                            </span>
                                        </NavLink>
                                        <NavLink to="/employee/calls" className={navLinkClass}>
                                            <CallsLabel notif={callsNotif} />
                                        </NavLink>
                                        <NavLink to="/employee/email" className={navLinkClass}>Email CRM</NavLink>
                                        <NavLink to="/employee/salary-slips" className={navLinkClass}>
                                            <span className="hidden xl:inline">Salary Slips</span>
                                            <span className="xl:hidden">Slips</span>
                                        </NavLink>
                                    </>
                                )}

                                <button onClick={logout} className={`${buttonStyle} flex-shrink-0`}>
                                    Logout
                                </button>

                                {user.role === 'employee' && (
                                    <Link to="/employee/profile">
                                        <img src={profile?.profilePictureUrl || `https://ui-avatars.com/api/?name=${user.name}&background=random`} alt="Profile" className="w-10 h-10 rounded-full object-cover ml-4 border-2 border-[#b8866f] hover:scale-105 transition" />
                                    </Link>
                                )}
                            </>
                        ) : (
                            <PublicDesktopNav isScrolled={isScrolled} location={location} />
                        )}
                        </div>{/* end inner scroll row */}
                    </div>{/* end relative scroll-fade-right wrapper */}

                    {/* Mobile Menu Button */}
                    {/* Hamburger — only on true mobile (<768px). On larger
                        screens the desktop nav handles overflow via scroll. */}
                    <div className="flex items-center md:hidden">
                        <button onClick={() => setMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'} className={`p-2 rounded-xl transition-all duration-300 ${isScrolled ? 'text-[#433020] hover:bg-gray-100' : 'text-white hover:bg-white/20'}`}>
                            {isMobileMenuOpen ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" /></svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* HR Human-Resources mega-menu (desktop) — rendered at navbar root
                with position:fixed so it can escape the horizontal-scroll
                container's overflow clip. Positioned via getBoundingClientRect
                of the trigger button on open + resize/scroll. */}
            {hrMenuOpen && (
                <div
                    id="hr-mega-menu"
                    className="fixed z-[60] w-[720px] max-w-[92vw] bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 animate-fade-in-up"
                    style={{ top: hrMenuPos.top, right: hrMenuPos.right }}
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* COLUMN 1 — People */}
                        <div>
                            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#8a6144] mb-2 pb-2 border-b border-[#8a6144]/15">People</div>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/manage-employees" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Employee Management</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/partners" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap flex items-center justify-between">
                                Business Partners
                                {pendingPartnersCount > 0 && (
                                    <span className="bg-[#8a6144] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full inline-flex items-center justify-center">
                                        {pendingPartnersCount}
                                    </span>
                                )}
                            </NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/partner-commissions" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Partner Commissions</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/register-employee" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Onboarding</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/documents" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">
                                Letters &amp; Documents
                                {isRecentlyShipped('2026-08-08') && <NewBadge />}
                            </NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/managers" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Team Management</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/task-performance" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Team Performance</NavLink>
                        </div>

                        {/* COLUMN 2 — Operations */}
                        <div>
                            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#8a6144] mb-2 pb-2 border-b border-[#8a6144]/15">Operations</div>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/approve-leave" className="px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap flex items-center justify-between">
                                <span>Leaves</span>
                                {pendingLeavesCount > 0 && <span className="bg-red-500 text-white rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-bold">{pendingLeavesCount}</span>}
                            </NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/announcements" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Announcements</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/holiday-calendar" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Holiday Calendar</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/whatsapp" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">💬 WhatsApp Assistant</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/ai-providers" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">🤖 AI Providers</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/portal-requests" className="px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap flex items-center justify-between gap-2">
                                <span>Portal Requests</span>
                                {portalReqNew > 0 && <span className="bg-red-500 text-white rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-bold">{portalReqNew}</span>}
                            </NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/penalties" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Mobile Logins</NavLink>
                        </div>

                        {/* COLUMN 3 — Reports */}
                        <div>
                            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#8a6144] mb-2 pb-2 border-b border-[#8a6144]/15">Reports</div>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/analytics" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">HR Analytics</NavLink>
                            {user.role === 'admin' && (
                                <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/site-analytics" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Website Traffic</NavLink>
                            )}
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/rankings" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Rankings</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/eod-reports" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">EOD Reports</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/calculator-report" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Calculator Report</NavLink>

                        </div>

                        {/* COLUMN 4 — Payroll */}
                        <div>
                            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#8a6144] mb-2 pb-2 border-b border-[#8a6144]/15">Payroll</div>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/salary-calculator" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Payroll Calculator</NavLink>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/salary-slips" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Salary Slips</NavLink>
                        </div>

                        {/* COLUMN 5 — Content */}
                        <div>
                            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#8a6144] mb-2 pb-2 border-b border-[#8a6144]/15">Content</div>
                            <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/blog" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Blog Posts</NavLink>
                            {user.role === 'admin' && (
                                <NavLink onClick={() => setHrMenuOpen(false)} to="/hr/reviews" className="block px-2 py-1.5 text-xs text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] rounded-md whitespace-nowrap">Customer Reviews</NavLink>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Leads dropdown — partner filters + commissions + targets, all in one */}
            {partnersOpen && (
                <PartnersDropdown
                    triggerRef={partnersTriggerRef}
                    onClose={() => setPartnersOpen(false)}
                    targetsData={targetsData}
                    targetsLoading={targetsLoading}
                    user={user}
                    leadsBasePath={user?.role === 'employee' ? '/employee/leads' : '/hr/leads'}
                />
            )}

            {/* Targets dropdown panel — rendered at navbar root with position:fixed
                so it escapes the horizontal-scroll clip. Lazy-loaded on first open. */}
            {targetsOpen && (
                <TargetsDropdown
                    data={targetsData}
                    loading={targetsLoading}
                    triggerRef={targetsTriggerRef}
                    user={user}
                    onClose={() => setTargetsOpen(false)}
                />
            )}

            {/* Mobile Menu — fixed: the inner panel is scrollable so the
                bottom items (Salary Slips, Portal Requests, Finance, etc.)
                aren't clipped on shorter screens. The outer wrapper still
                handles the open/close slide animation. */}
            <div className={`absolute top-full left-0 w-full overflow-hidden transition-all duration-300 ease-in-out md:hidden ${isMobileMenuOpen ? 'max-h-[calc(100vh-4rem)] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className={`bg-white/95 backdrop-blur-xl shadow-2xl border-t border-gray-100 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain custom-scrollbar`}>
                    <div className={`px-4 pt-4 pb-6 space-y-2`}>
                        {user ? (
                            <>
                                {(user.role === 'hr' || user.role === 'admin') && (
                                    <>
                                        <NavLink to="/hr/dashboard" onClick={handleLinkClick} className={mobileNavLinkClass}>Dashboard</NavLink>
                                        <NavLink to="/hr/tasks" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <span className="flex items-center justify-between w-full">
                                                Tasks
                                                <MobileBadge pending={taskNotif.pending} overdue={taskNotif.overdue} />
                                            </span>
                                        </NavLink>
                                        {user.role === 'admin' && (
                                            <>
                                                <NavLink to="/hr/leads" onClick={handleLinkClick} className={mobileNavLinkClass}>Leads</NavLink>
                                                <NavLink to="/hr/targets" onClick={handleLinkClick} className={mobileNavLinkClass}>Targets</NavLink>
                                            </>
                                        )}
                                        <NavLink to="/hr/meetings" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <span className="flex items-center justify-between w-full">
                                                Meetings
                                                <MeetingMobileBadge notif={meetingNotif} />
                                            </span>
                                        </NavLink>
                                        <NavLink to="/hr/projects" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <span className="flex items-center justify-between w-full">
                                                Projects
                                                <ProjectMobileBadge notif={projectNotif} />
                                            </span>
                                        </NavLink>
                                        <NavLink to="/hr/expenses" onClick={handleLinkClick} className={mobileNavLinkClass}>Expenses</NavLink>
                                        <NavLink to="/hr/assets" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <span className="flex items-center justify-between w-full">
                                                Assets
                                                <AssetMobileBadge notif={assetNotif} />
                                            </span>
                                        </NavLink>
                                        {(billingPerm.canCreate || billingPerm.canViewAll) && (
                                            <NavLink to="/hr/invoices" onClick={handleLinkClick} className={mobileNavLinkClass}>Billing</NavLink>
                                        )}
                                        <NavLink to="/hr/kpis" onClick={handleLinkClick} className={mobileNavLinkClass}>KPIs</NavLink>
                                        <div className="w-full h-px bg-gray-200 my-2"></div>
                                        <div className="text-xs font-bold text-gray-400 uppercase px-4 mb-2">HR Operations</div>
                                        <NavLink to="/hr/approve-leave" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            Approve Leave {pendingLeavesCount > 0 && <span className="ml-2 bg-red-600 text-white rounded-full px-2 py-0.5 text-xs">{pendingLeavesCount}</span>}
                                        </NavLink>
                                        <NavLink to="/hr/analytics" onClick={handleLinkClick} className={mobileNavLinkClass}>Analytics</NavLink>
                                        <NavLink to="/hr/rankings" onClick={handleLinkClick} className={mobileNavLinkClass}>Rankings</NavLink>
                                        <NavLink to="/hr/manage-employees" onClick={handleLinkClick} className={mobileNavLinkClass}>Manage Emp.</NavLink>
                                        <NavLink to="/hr/partners" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <div className="flex items-center justify-between">
                                                <span>Business Partners</span>
                                                {pendingPartnersCount > 0 && (
                                                    <span className="bg-[#fff5e6] text-[#8a6144] text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                                                        {pendingPartnersCount}
                                                    </span>
                                                )}
                                            </div>
                                        </NavLink>
                                        <NavLink to="/hr/partner-commissions" onClick={handleLinkClick} className={mobileNavLinkClass}>💰 Partner Commissions</NavLink>
                                        <NavLink to="/hr/register-employee" onClick={handleLinkClick} className={mobileNavLinkClass}>Register Emp.</NavLink>
                                        <NavLink to="/hr/managers" onClick={handleLinkClick} className={mobileNavLinkClass}>Managers</NavLink>
                                        <NavLink to="/hr/salary-calculator" onClick={handleLinkClick} className={mobileNavLinkClass}>Salary Calculator</NavLink>
                                        <NavLink to="/hr/eod-reports" onClick={handleLinkClick} className={mobileNavLinkClass}>EOD Reports</NavLink>
                                        <NavLink to="/hr/calculator-report" onClick={handleLinkClick} className={mobileNavLinkClass}>Calculator Report</NavLink>
                                        <NavLink to="/hr/announcements" onClick={handleLinkClick} className={mobileNavLinkClass}>Announcements</NavLink>
                                        <NavLink to="/hr/penalties" onClick={handleLinkClick} className={mobileNavLinkClass}>Mobile Logins</NavLink>
                                        <NavLink to="/hr/salary-slips" onClick={handleLinkClick} className={mobileNavLinkClass}>Salary Slips</NavLink>
                                        <NavLink to="/hr/portal-requests" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <div className="flex items-center justify-between">
                                                <span>Portal Requests</span>
                                                {portalReqNew > 0 && (
                                                    <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                                                        {portalReqNew}
                                                    </span>
                                                )}
                                            </div>
                                        </NavLink>
                                        {user.role === 'admin' && (
                                            <NavLink to="/hr/finance" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                                💰 Finance Tracker
                                            </NavLink>
                                        )}
                                        <NavLink to="/admin/calls" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <span className="flex items-center justify-between w-full">
                                                <span>✨ Calls</span>
                                                <CallsMobileBadge notif={callsNotif} />
                                            </span>
                                        </NavLink>
                                        <div className="w-full h-px bg-gray-200 my-2"></div>
                                    </>
                                )}
                                {user.role === 'partner' && (
                                    <>
                                        <NavLink to="/partner/dashboard" onClick={handleLinkClick} className={mobileNavLinkClass}>Dashboard</NavLink>
                                    </>
                                )}

                                {user.role === 'employee' && (
                                    <>
                                        <NavLink to="/employee/dashboard" onClick={handleLinkClick} className={mobileNavLinkClass}>Dashboard</NavLink>
                                        <NavLink to="/employee/tasks" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <span className="flex items-center justify-between w-full">
                                                Tasks
                                                <MobileBadge pending={taskNotif.pending} overdue={taskNotif.overdue} />
                                            </span>
                                        </NavLink>
                                        <NavLink to="/employee/leads" onClick={handleLinkClick} className={mobileNavLinkClass}>Leads</NavLink>
                                        {user.isManager && user.hasLeadsAccess && (
                                            <NavLink to="/employee/targets" onClick={handleLinkClick} className={mobileNavLinkClass}>Targets</NavLink>
                                        )}
                                        <NavLink to={(user.canBlog || profile?.canBlog) ? '/employee/blog' : '/blog'} onClick={handleLinkClick} className={mobileNavLinkClass}>Blog</NavLink>
                                        <NavLink to="/employee/meetings" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <span className="flex items-center justify-between w-full">
                                                Meetings
                                                <MeetingMobileBadge notif={meetingNotif} />
                                            </span>
                                        </NavLink>
                                        <NavLink to="/employee/projects" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <span className="flex items-center justify-between w-full">
                                                Projects
                                                <ProjectMobileBadge notif={projectNotif} />
                                            </span>
                                        </NavLink>
                                        <NavLink to="/employee/expenses" onClick={handleLinkClick} className={mobileNavLinkClass}>Expenses</NavLink>
                                        <NavLink to="/employee/assets" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <span className="flex items-center justify-between w-full">
                                                Assets
                                                <AssetMobileBadge notif={assetNotif} />
                                            </span>
                                        </NavLink>
                                        {(billingPerm.canCreate || billingPerm.canViewAll) && (
                                            <NavLink to="/employee/invoices" onClick={handleLinkClick} className={mobileNavLinkClass}>Billing</NavLink>
                                        )}
                                        {user.isManager && (
                                            <NavLink to="/employee/kpis" onClick={handleLinkClick} className={mobileNavLinkClass}>KPIs</NavLink>
                                        )}
                                        <NavLink to="/employee/rankings" onClick={handleLinkClick} className={mobileNavLinkClass}>Rankings</NavLink>
                                        <NavLink to="/employee/apply-leave" onClick={handleLinkClick} className={mobileNavLinkClass}>Apply for Leave</NavLink>
                                        <NavLink to="/employee/announcements" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            Announcements {hasUnread && <span className="ml-2 bg-red-600 text-white rounded-full px-2 py-0.5 text-xs">New</span>}
                                        </NavLink>
                                        <NavLink to="/employee/calls" onClick={handleLinkClick} className={mobileNavLinkClass}>
                                            <span className="flex items-center justify-between w-full">
                                                <span>✨ Calls</span>
                                                <CallsMobileBadge notif={callsNotif} />
                                            </span>
                                        </NavLink>
                                        <NavLink to="/employee/salary-slips" onClick={handleLinkClick} className={mobileNavLinkClass}>Salary Slips</NavLink>
                                        <NavLink to="/employee/profile" onClick={handleLinkClick} className={mobileNavLinkClass}>Profile</NavLink>
                                    </>
                                )}
                                <div className="pt-4 pb-3 border-t border-gray-700">
                                    <div className="flex items-center px-5 mb-3">
                                        <div className="flex-shrink-0">
                                            <img className="h-10 w-10 rounded-full object-cover border-2 border-[#b8866f]" src={profile?.profilePictureUrl || `https://ui-avatars.com/api/?name=${user.name}&background=random`} alt="" />
                                        </div>
                                        <div className="ml-3">
                                            <div className={`text-base font-medium leading-none ${isScrolled ? 'text-[#433020]' : 'text-white'}`}>{user.name}</div>
                                            <div className={`text-sm font-medium leading-none mt-1 ${isScrolled ? 'text-gray-500' : 'text-gray-400'}`}>{user.email}</div>
                                        </div>
                                    </div>
                                    <button onClick={() => { logout(); handleLinkClick(); }} className={`w-full mt-4 bg-red-50 text-red-600 font-bold py-3 rounded-xl hover:bg-red-100 transition-colors`}>
                                        Logout
                                    </button>
                                </div>
                            </>
                        ) : (
                            <PublicMobileNav onClose={handleLinkClick} />
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
};

// Inline label shown on the desktop Tasks navlink. Renders a red dot with the count
// of currently-pending tasks, plus a subtle overdue badge if anything's past deadline.
const TaskLabel = ({ pending, overdue }) => {
    const display = (n) => (n > 99 ? '99+' : n);
    return (
        <span className="relative inline-flex items-center gap-1.5">
            <span>Tasks</span>
            {pending > 0 && (
                <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shadow-md ring-2 ring-white/30"
                    title={`${pending} pending task${pending === 1 ? '' : 's'}`}
                >
                    {display(pending)}
                </span>
            )}
            {overdue > 0 && (
                <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none shadow-md"
                    title={`${overdue} overdue`}
                >
                    !{display(overdue)}
                </span>
            )}
        </span>
    );
};

// Meetings nav badge — shows today's count, with the badge turning emerald
// (and pulsing) if a meeting is currently happening, or amber if one starts
// within the hour. Tooltip shows the next meeting time + title.
const MeetingLabel = ({ notif }) => {
    if (!notif || (notif.today === 0 && notif.happeningNow === 0)) {
        return <span>Meetings</span>;
    }
    const tooltip = notif.nextMeeting
        ? `Next: ${new Date(notif.nextMeeting.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · ${notif.nextMeeting.title}`
        : `${notif.today} meeting${notif.today === 1 ? '' : 's'} today`;
    const badgeClass = notif.happeningNow > 0
        ? 'bg-emerald-500 animate-soft-pulse'
        : notif.startingSoon > 0
            ? 'bg-amber-500 animate-soft-pulse'
            : 'bg-[#8a6144]';
    return (
        <span className="relative inline-flex items-center gap-1.5">
            <span>Meetings</span>
            <span
                className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-white text-[10px] font-bold leading-none shadow-md ring-2 ring-white/30 ${badgeClass}`}
                title={tooltip}
            >
                {notif.today > 99 ? '99+' : notif.today}
            </span>
        </span>
    );
};

const MeetingMobileBadge = ({ notif }) => {
    if (!notif || (notif.today === 0 && notif.happeningNow === 0)) return null;
    return (
        <span className="flex items-center gap-1.5 ml-2">
            {notif.happeningNow > 0 && <span className="bg-emerald-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold animate-soft-pulse">LIVE</span>}
            {notif.today > 0 && <span className="bg-[#8a6144] text-white rounded-full px-2 py-0.5 text-[10px] font-bold">{notif.today} today</span>}
        </span>
    );
};

// Projects nav badge — shows ongoing count, with a red dot/pulse if there
// are sign-offs awaiting this user (since those are personal action items).
const ProjectLabel = ({ notif }) => {
    if (!notif || (notif.ongoing === 0 && notif.awaitingMySignOff === 0)) {
        return <span>Projects</span>;
    }
    const display = (n) => (n > 99 ? '99+' : n);
    const tooltip = notif.awaitingMySignOff > 0
        ? `${notif.awaitingMySignOff} project${notif.awaitingMySignOff === 1 ? '' : 's'} awaiting your sign-off`
        : `${notif.ongoing} ongoing project${notif.ongoing === 1 ? '' : 's'}`;
    return (
        <span className="relative inline-flex items-center gap-1.5">
            <span>Projects</span>
            {notif.awaitingMySignOff > 0 && (
                <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shadow-md ring-2 ring-white/30 animate-soft-pulse"
                    title={tooltip}
                >
                    ✓{display(notif.awaitingMySignOff)}
                </span>
            )}
            {notif.ongoing > 0 && notif.awaitingMySignOff === 0 && (
                <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#8a6144] text-white text-[10px] font-bold leading-none shadow-md ring-2 ring-white/30"
                    title={tooltip}
                >
                    {display(notif.ongoing)}
                </span>
            )}
            {notif.overdue > 0 && (
                <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none shadow-md"
                    title={`${notif.overdue} overdue`}
                >
                    !{display(notif.overdue)}
                </span>
            )}
        </span>
    );
};

const ProjectMobileBadge = ({ notif }) => {
    if (!notif || (notif.ongoing === 0 && notif.awaitingMySignOff === 0)) return null;
    return (
        <span className="flex items-center gap-1.5 ml-2">
            {notif.awaitingMySignOff > 0 && (
                <span className="bg-red-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold animate-soft-pulse">
                    {notif.awaitingMySignOff} sign-off
                </span>
            )}
            {notif.ongoing > 0 && (
                <span className="bg-[#8a6144] text-white rounded-full px-2 py-0.5 text-[10px] font-bold">
                    {notif.ongoing} ongoing
                </span>
            )}
            {notif.overdue > 0 && (
                <span className="bg-amber-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold">
                    {notif.overdue} overdue
                </span>
            )}
        </span>
    );
};

// Assets nav badge — pulses red when an asset has been assigned to *me*
// (personal action: I need to acknowledge receipt). Shows a brown count when
// I'm just an admin/manager watching team-wide pending acknowledgments.
function AssetLabel({ notif }) {
    if (!notif || (notif.myPending === 0 && notif.totalPending === 0)) {
        return <span>Assets</span>;
    }
    const display = (n) => (n > 99 ? '99+' : n);
    const tooltip = notif.myPending > 0
        ? `${notif.myPending} asset${notif.myPending === 1 ? '' : 's'} awaiting your acknowledgment`
        : `${notif.totalPending} pending asset acknowledgment${notif.totalPending === 1 ? '' : 's'} on your team`;
    return (
        <span className="relative inline-flex items-center gap-1.5">
            <span>Assets</span>
            {notif.myPending > 0 ? (
                <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shadow-md ring-2 ring-white/30 animate-soft-pulse"
                    title={tooltip}
                >
                    📥{display(notif.myPending)}
                </span>
            ) : (
                <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#8a6144] text-white text-[10px] font-bold leading-none shadow-md ring-2 ring-white/30"
                    title={tooltip}
                >
                    {display(notif.totalPending)}
                </span>
            )}
        </span>
    );
}

function AssetMobileBadge({ notif }) {
    if (!notif || (notif.myPending === 0 && notif.totalPending === 0)) return null;
    return (
        <span className="flex items-center gap-1.5 ml-2">
            {notif.myPending > 0 && (
                <span className="bg-red-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold animate-soft-pulse">
                    📥 {notif.myPending} to ack
                </span>
            )}
            {notif.myPending === 0 && notif.totalPending > 0 && (
                <span className="bg-[#8a6144] text-white rounded-full px-2 py-0.5 text-[10px] font-bold">
                    {notif.totalPending} pending
                </span>
            )}
        </span>
    );
};

const MobileBadge = ({ pending, overdue }) => {
    if (!pending && !overdue) return null;
    return (
        <span className="flex items-center gap-1.5 ml-2">
            {pending > 0 && (
                <span className="bg-red-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold">
                    {pending > 99 ? '99+' : pending} pending
                </span>
            )}
            {overdue > 0 && (
                <span className="bg-amber-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold">
                    {overdue > 99 ? '99+' : overdue} overdue
                </span>
            )}
        </span>
    );
};

// Desktop pill for the admin Calls link. Surfaces a single dynamic badge
// based on urgency: "pendingSoon" (next hour) trumps "dueToday", which
// trumps "overdue". Always shows the small admin "A" pill alongside.
const CallsLabel = ({ notif }) => {
    const { dueToday = 0, overdue = 0, pendingSoon = 0 } = notif || {};
    let badge = null;
    if (pendingSoon > 0) {
        badge = (
            <span className="bg-orange-500 text-white rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-extrabold animate-pulse">
                {pendingSoon > 99 ? '99+' : pendingSoon}
            </span>
        );
    } else if (dueToday > 0) {
        badge = (
            <span className="bg-emerald-500 text-white rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-extrabold">
                {dueToday > 99 ? '99+' : dueToday}
            </span>
        );
    } else if (overdue > 0) {
        badge = (
            <span className="bg-rose-500 text-white rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-extrabold">
                {overdue > 99 ? '99+' : overdue}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1">
            ✨ Calls
            {badge}
        </span>
    );
};

// Mobile menu equivalent — full-text labels since there's no width constraint.
const CallsMobileBadge = ({ notif }) => {
    const { dueToday = 0, overdue = 0, pendingSoon = 0 } = notif || {};
    if (!dueToday && !overdue && !pendingSoon) return null;
    return (
        <span className="flex items-center gap-1.5 ml-2">
            {pendingSoon > 0 && (
                <span className="bg-orange-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold animate-pulse">
                    {pendingSoon > 99 ? '99+' : pendingSoon} soon
                </span>
            )}
            {dueToday > 0 && (
                <span className="bg-emerald-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold">
                    {dueToday > 99 ? '99+' : dueToday} today
                </span>
            )}
            {overdue > 0 && (
                <span className="bg-rose-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold">
                    {overdue > 99 ? '99+' : overdue} overdue
                </span>
            )}
        </span>
    );
};

// =============================================================================
// TARGETS DROPDOWN — floating panel anchored under the Targets button.
// Shows current-month targets with progress bars.
// =============================================================================

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CURRENCY_SYMBOLS_NAV = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };

const fmtNav = (n, currency = 'INR') => {
    const sym = CURRENCY_SYMBOLS_NAV[currency] || currency;
    if (n >= 1e7) return `${sym}${(n / 1e7).toFixed(2)}Cr`;
    if (n >= 1e5) return `${sym}${(n / 1e5).toFixed(2)}L`;
    if (n >= 1e3) return `${sym}${(n / 1e3).toFixed(1)}K`;
    return `${sym}${n.toLocaleString('en-IN')}`;
};

const TargetsDropdown = ({ data, loading, triggerRef, user, onClose }) => {
    const [pos, setPos] = React.useState({ top: 0, right: 0 });

    React.useEffect(() => {
        const place = () => {
            const r = triggerRef.current?.getBoundingClientRect();
            if (!r) return;
            setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [triggerRef]);

    const targets = data?.targets || [];
    const month   = data?.month;
    const year    = data?.year;
    const isAdmin    = user?.role === 'admin' || user?.role === 'hr';
    const targetPath = isAdmin ? '/hr/targets' : '/employee/targets';

    return (
        <div
            id="targets-dropdown"
            className="fixed z-[60] w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 animate-fade-in-up overflow-hidden"
            style={{ top: pos.top, right: pos.right }}
        >
            {/* Targets section header */}
            <div className="flex items-center justify-between px-4 py-2 bg-[#fdf8f4]/60 border-b border-gray-100">
                <div className="flex items-center gap-1.5">
                    <span className="text-sm">🎯</span>
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#8a6144]">
                        Sales Targets
                        {month && year && <span className="ml-1 font-medium normal-case tracking-normal text-gray-400">— {MONTHS_SHORT[month - 1]} {year}</span>}
                    </span>
                </div>
                <Link to={targetPath} onClick={onClose} className="text-[10px] font-bold text-[#8a6144] hover:underline">
                    Manage →
                </Link>
            </div>

            {/* Target rows */}
            <div className="max-h-60 overflow-y-auto">
                {loading && (
                    <div className="flex justify-center items-center py-6">
                        <div className="w-4 h-4 rounded-full border-2 border-[#8a6144] border-t-transparent animate-spin" />
                    </div>
                )}
                {!loading && targets.length === 0 && (
                    <div className="text-center py-6 text-gray-400">
                        <div className="text-xs">No targets set this month</div>
                        {(isAdmin || user?.isManager) && (
                            <Link to={targetPath} onClick={onClose} className="inline-block mt-1 text-[10px] font-bold text-[#8a6144] hover:underline">
                                Set targets →
                            </Link>
                        )}
                    </div>
                )}
                {!loading && targets.map((t) => {
                    const achieved = t.achievedAmount || 0;
                    const target   = t.targetAmount || 0;
                    const p        = target > 0 ? Math.min(100, Math.round((achieved / target) * 100)) : 0;
                    const barColor = p >= 100 ? 'bg-emerald-500' : p >= 60 ? 'bg-amber-500' : 'bg-rose-500';
                    const name     = t.assignedTo?.name || 'Unknown';
                    return (
                        <div key={t._id} className="px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-[#fdf8f4]/60 transition">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-semibold text-gray-700 truncate max-w-[140px]">{name}</span>
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                    p >= 100 ? 'bg-emerald-100 text-emerald-700' :
                                    p >= 60  ? 'bg-amber-100 text-amber-700' :
                                               'bg-rose-100 text-rose-600'
                                }`}>{p}%</span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-1">
                                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${p}%` }} />
                            </div>
                            <div className="flex justify-between text-[10px] text-gray-400">
                                <span>{fmtNav(achieved, t.currency)} won</span>
                                <span>of {fmtNav(target, t.currency)}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// =============================================================================
// PARTNERS DROPDOWN — floating panel anchored under the 🤝 button.
// Shows approved partners as filter links + commissions page link.
// =============================================================================

const PartnersDropdown = ({ triggerRef, onClose, targetsData, targetsLoading, user, leadsBasePath = '/hr/leads' }) => {
    const [pos, setPos] = React.useState({ top: 0, right: 0 });

    React.useEffect(() => {
        const place = () => {
            const r = triggerRef.current?.getBoundingClientRect();
            if (!r) return;
            setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [triggerRef]);

    const targets = targetsData?.targets || [];
    const month = targetsData?.month;
    const year = targetsData?.year;
    const isAdmin = user?.role === 'admin' || user?.role === 'hr';

    return (
        <div
            id="partners-dropdown"
            className="fixed z-[60] w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 animate-fade-in-up overflow-hidden"
            style={{ top: pos.top, right: pos.right }}
        >
            {/* Leads section */}
            <div className="border-b border-gray-100">
                <Link to={leadsBasePath} onClick={onClose}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-[#fdf8f4] transition-colors">
                    <span className="text-xs font-semibold text-gray-700">All Leads</span>
                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
                </Link>
                <Link to={`${leadsBasePath}?type=partner`} onClick={onClose}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-[#fdf8f4] transition-colors">
                    <span className="text-xs font-semibold text-gray-700">🤝 Partner Leads</span>
                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
                </Link>
                {user?.role !== 'employee' && (
                    <Link to="/hr/partner-commissions" onClick={onClose}
                        className="flex items-center justify-between px-4 py-2.5 hover:bg-[#fdf8f4] transition-colors">
                        <span className="text-xs font-semibold text-gray-700">💰 Partner Commissions</span>
                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
                    </Link>
                )}
            </div>

            {/* Targets section */}
            <div className="flex items-center justify-between px-4 py-1.5 bg-[#fdf8f4]/60 border-b border-gray-100">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#8a6144]">
                    🎯 Targets{month && year ? ` — ${MONTHS_SHORT[month - 1]} ${year}` : ''}
                </span>
                <Link to={isAdmin ? '/hr/targets' : '/employee/targets'} onClick={onClose}
                    className="text-[10px] font-bold text-[#8a6144] hover:underline">Manage →</Link>
            </div>
            <div className="max-h-44 overflow-y-auto">
                {targetsLoading && (
                    <div className="flex justify-center py-4">
                        <div className="w-4 h-4 rounded-full border-2 border-[#8a6144] border-t-transparent animate-spin" />
                    </div>
                )}
                {!targetsLoading && targets.length === 0 && (
                    <div className="text-center py-4 text-xs text-gray-400">No targets set this month</div>
                )}
                {!targetsLoading && targets.map((t) => {
                    const achieved = t.achievedAmount || 0;
                    const target = t.targetAmount || 0;
                    const p = target > 0 ? Math.min(100, Math.round((achieved / target) * 100)) : 0;
                    const barColor = p >= 100 ? 'bg-emerald-500' : p >= 60 ? 'bg-amber-500' : 'bg-rose-500';
                    return (
                        <div key={t._id} className="px-4 py-2.5 border-b border-gray-50 last:border-0 hover:bg-[#fdf8f4]/60 transition">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold text-gray-700 truncate max-w-[150px]">{t.assignedTo?.name || 'Unknown'}</span>
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${p >= 100 ? 'bg-emerald-100 text-emerald-700' : p >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-600'}`}>{p}%</span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${p}%` }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// =============================================================================
// PUBLIC NAV — what unauthenticated visitors (homepage browsers) see.
// Centered pill of nav links + refined Sign-in / Get-access CTAs.
// =============================================================================

const PUBLIC_LINKS = [
    { label: 'Home',         to: '/',                 hash: '' },
    { label: 'Features',     to: '/#features',        hash: '#features' },
    { label: 'Modules',      to: '/#features',        hash: '#features' },
    { label: 'Roles',        to: '/#built-for',       hash: '#built-for' },
    { label: 'Blog',         to: '/blog',             hash: '', route: true },
    { label: 'Contact',      to: '/#request-access',  hash: '#request-access' }
];

const PublicDesktopNav = ({ isScrolled, location }) => {
    const isActive = (link) => {
        if (link.route) return location.pathname.startsWith(link.to);
        if (link.hash === '') return location.pathname === '/' && !location.hash;
        return location.hash === link.hash;
    };
    return (
        <>
            {/* Centered pill of nav links */}
            <div className={`absolute left-1/2 -translate-x-1/2 hidden md:flex items-center gap-0.5 rounded-full p-1 backdrop-blur-md transition-colors ${isScrolled ? 'bg-white/80 border border-[#8a6144]/15 shadow-sm' : 'bg-white/10 border border-white/15'}`}>
                {PUBLIC_LINKS.slice(0, 5).map((l) => {
                    const active = isActive(l);
                    return (
                        <Link
                            key={l.label}
                            to={l.to}
                            className={`text-[11px] lg:text-xs font-bold px-3.5 lg:px-4 py-1.5 rounded-full transition-all whitespace-nowrap ${
                                active
                                    ? (isScrolled ? 'bg-[#433020] text-white shadow-md' : 'bg-white text-[#433020] shadow-md')
                                    : (isScrolled ? 'text-[#5d4332] hover:text-[#433020] hover:bg-[#fff5e6]' : 'text-white/90 hover:text-white hover:bg-white/10')
                            }`}
                        >
                            {l.label}
                        </Link>
                    );
                })}
            </div>

            {/* Right-side CTAs */}
            <div className="hidden md:flex items-center gap-2 ml-auto">
                {/* Play Store link. rel="noopener" because target="_blank"
                    otherwise hands the new tab a reference back to this window. */}
                <a
                    href="https://play.google.com/store/apps/details?id=com.avanienterprises.Hr"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Download the Avani HR Portal app on Google Play"
                    className={`hidden lg:inline-flex items-center gap-1.5 text-[11px] lg:text-xs font-bold px-3 py-2 rounded-full transition-colors ${
                        isScrolled
                            ? 'text-[#5d4332] hover:text-[#433020] hover:bg-[#fff5e6]'
                            : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                >
                    <Smartphone className="w-3.5 h-3.5" /> Get the app
                </a>
                <Link
                    to="/login"
                    className={`text-[11px] lg:text-xs font-bold px-4 py-2 rounded-full transition-colors ${
                        isScrolled
                            ? 'text-[#5d4332] hover:text-[#433020] hover:bg-[#fff5e6]'
                            : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                >
                    Sign in
                </Link>
                <Link
                    to="/#request-access"
                    className={`inline-flex items-center gap-1 text-[11px] lg:text-xs font-bold px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 ${
                        isScrolled
                            ? 'bg-gradient-to-br from-[#433020] to-[#2d2017] text-white hover:from-[#2d2017] hover:to-[#1a1208]'
                            : 'bg-[#fff5e6] text-[#433020] hover:bg-white'
                    }`}
                >
                    Get access
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
                </Link>
            </div>
        </>
    );
};

const PublicMobileNav = ({ onClose }) => (
    <div className="space-y-1.5">
        {PUBLIC_LINKS.map((l) => (
            <Link
                key={l.label}
                to={l.to}
                onClick={onClose}
                className="block px-4 py-3 rounded-xl text-base font-semibold text-gray-700 hover:bg-[#fff5e6] hover:text-[#8a6144] transition-colors"
            >
                {l.label}
            </Link>
        ))}
        <div className="pt-3 mt-3 border-t border-gray-200 flex flex-col gap-2.5">
            <Link
                to="/login"
                onClick={onClose}
                className="w-full bg-gray-50 text-gray-800 font-bold py-3 text-center rounded-xl hover:bg-gray-100 transition-colors"
            >
                Sign in
            </Link>
            <Link
                to="/#request-access"
                onClick={onClose}
                className="w-full bg-gradient-to-br from-[#433020] to-[#2d2017] text-white font-bold py-3 text-center rounded-xl shadow-md hover:from-[#2d2017] hover:to-[#1a1208] transition-colors"
            >
                Get access
            </Link>
        </div>
    </div>
);

export default Navbar;
